import uuidV4 from 'lib/string/uuidV4Utils'
import type { IntlShape } from 'react-intl'
import { CHECKOUT_ITEM_TYPE_GIFT_CARD } from 'constants/checkout'
import config from 'constants/config'
import type { Wedding, WeddingGift } from 'weddings/api/weddings'
import { getSimpleImageUrl } from 'lib/image/imageUtils'
import { nonNullable } from 'lib/array/arrayUtils'

export function generateGiftCardItem(amount: number): App.Checkout.GiftCardItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_GIFT_CARD,
    amount,
    deliveryMethod: 'email',
    content: {},
  }
}

export interface WeddingGiftCardDonor {
  firstName: string
  lastName: string
  message?: string
}

export function generateWeddingGiftCardItem(
  wedding: Wedding,
  gift: WeddingGift,
  amount: number,
  intl: IntlShape,
  donor: WeddingGiftCardDonor,
): App.Checkout.GiftCardItem {
  const coupleName = intl.formatMessage(
    {
      defaultMessage: '{partnerOneName} & {partnerTwoName}',
      id: 'n1JDIcOsu1qAnzeSiveI',
      description: 'Display name for a wedding couple shown in wedding gifting flows.',
    },
    {
      partnerOneName: wedding.partnerOneFirstName,
      partnerTwoName: wedding.partnerTwoFirstName,
    },
  )

  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_GIFT_CARD,
    amount,
    deliveryMethod: 'email',
    content: {
      recipientName: coupleName,
      recipientEmail: wedding.notificationEmail || undefined,
      fromName: `${donor.firstName} ${donor.lastName}`,
      message: donor.message,
    },
    product: {
      image: { id: gift.imageId, url: getSimpleImageUrl(gift.imageId, 'jpeg') },
      name: gift.title,
      items: nonNullable([gift.id]),
      type: 'wedding-gift-card',
      location: wedding.venueAddress ?? '',
      url: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/weddings/${wedding.slug}`,
    },
  }
}

export function generateExperienceGiftCardItem(
  experience: App.ExperienceOffer,
  amount: number,
  intl: IntlShape,
  ticketCounts: Array<{ ticket: App.ExperienceItemTicket, count: number }>,
): App.Checkout.GiftCardItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_GIFT_CARD,
    amount,
    deliveryMethod: 'email',
    content: {},
    product: {
      image: experience.images[0],
      items: ticketCounts.map((ticketItem) => {
        if (ticketItem.count <= 1) {
          return ticketItem.ticket.name
        }

        return intl.formatMessage(
          {
            defaultMessage: '{ticketName} × {count}',
            id: 'HDi58lZcIUqlDLvYcmZH',
            description: 'Label showing an experience ticket name with selected quantity.',
          },
          {
            ticketName: ticketItem.ticket.name,
            count: ticketItem.count,
          },
        )
      }),
      name: experience.name,
      type: intl.formatMessage({
        defaultMessage: 'experience',
        id: '0x-tjqXw1Zzg1x9DMA3v',
        description: 'Generic product type label for experience gift cards.',
      }),
      location: experience.location.name,
      url: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/experience/${experience.slug}/${experience.id}`,
    },
  }
}
